const API_BASE = "https://api.jsonbin.io/v3";

export interface Bin {
  record: any;
  metadata: {
    id: string;
    name: string;
    createdAt: string;
    private: boolean;
  };
}

export interface BinListItem {
  snippetMeta: Record<string, any>;
  private: boolean;
  record: string;
  createdAt: string;
  name?: string;
}

export type BinListResponse = BinListItem[];

export const getToken = (): string | null => {
  return localStorage.getItem("jsonbin_token");
};

export const setToken = (token: string): void => {
  localStorage.setItem("jsonbin_token", token);
};

export const removeToken = (): void => {
  localStorage.removeItem("jsonbin_token");
};

export const fetchBin = async (binId: string): Promise<Bin> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const response = await fetch(`${API_BASE}/b/${binId}`, {
    headers: {
      "X-Master-Key": token,
    },
  });

  if (!response.ok) {
    throw new Error(`获取失败: ${response.statusText}`);
  }

  return response.json();
};

const isASCII = (str: string): boolean => {
  return /^[\x00-\x7F]*$/.test(str);
};

export const createBin = async (data: any, name?: string, isPrivate: boolean = true): Promise<Bin> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Master-Key": token,
    "X-Bin-Private": String(isPrivate),
  };

  if (name && name.trim()) {
    const trimmedName = name.trim();
    if (isASCII(trimmedName)) {
      headers["X-Bin-Name"] = trimmedName;
    } else {
      throw new Error("Bin 名称只能包含 ASCII 字符（不支持中文）");
    }
  }

  const response = await fetch(`${API_BASE}/b`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`创建失败: ${response.statusText}`);
  }

  return response.json();
};

export const updateBin = async (binId: string, data: any): Promise<Bin> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const response = await fetch(`${API_BASE}/b/${binId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Master-Key": token,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`更新失败: ${response.statusText}`);
  }

  return response.json();
};

export const updateBinName = async (binId: string, name: string): Promise<void> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const trimmedName = name.trim();
  if (!isASCII(trimmedName)) {
    throw new Error("Bin 名称只能包含 ASCII 字符（不支持中文）");
  }

  const response = await fetch(`${API_BASE}/b/${binId}/meta/name`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Master-Key": token,
      "X-Bin-Name": trimmedName,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`更新名称失败: ${response.statusText}`);
  }
};

export const deleteBin = async (binId: string): Promise<void> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const response = await fetch(`${API_BASE}/b/${binId}`, {
    method: "DELETE",
    headers: {
      "X-Master-Key": token,
    },
  });

  if (!response.ok) {
    throw new Error(`删除失败: ${response.statusText}`);
  }
};

export const updateBinPrivate = async (binId: string, isPrivate: boolean): Promise<void> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const response = await fetch(`${API_BASE}/b/${binId}/meta/privacy`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Master-Key": token,
      "X-Bin-Private": String(isPrivate),
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`更新私有状态失败: ${response.statusText}`);
  }
};

export const listBins = async (): Promise<BinListResponse> => {
  const token = getToken();
  if (!token) throw new Error("未设置 Token");

  const response = await fetch(`${API_BASE}/c/uncategorized/bins`, {
    headers: {
      "X-Master-Key": token,
    },
  });

  if (!response.ok) {
    throw new Error(`获取列表失败: ${response.statusText}`);
  }

  return response.json();
};
